import { ArrowUpRight } from "lucide-react";
import { STARTER_KIT_URL } from "@/lib/translations";
import { trackEvent } from "@/lib/analytics";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/525fc0b0-ec72-4c46-9b73-6555f1260a94/images/262bf3e32ee8aca6ae4d5083a8ce364bda676f5e210a1d084d2d28c58d20bdd2.png";

export default function Hero({ t }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-40 pb-24 md:pt-48 md:pb-32 overflow-hidden"
    >
      {/* Background image w/ heavy fade */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-b from-[#f9f6f0]/40 via-[#f9f6f0]/70 to-[#f9f6f0]"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <p className="text-overline reveal" data-testid="hero-overline">
          {t.hero.overline}
        </p>

        <h1
          className="font-serif font-light tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-8xl mt-6 max-w-4xl reveal reveal-delay-1"
          data-testid="hero-title"
        >
          {t.hero.title}
        </h1>

        <p
          className="mt-8 max-w-xl text-lg leading-relaxed text-[#5c6b61] reveal reveal-delay-2"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </p>

        {/* Dual CTA paths */}
        <div className="mt-12 grid sm:grid-cols-2 gap-4 max-w-3xl reveal reveal-delay-3">
          <a
            href={STARTER_KIT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-insider"
            onClick={() => trackEvent("starter_kit_click", { event_category: "conversion", source: "hero" })}
            className="group relative flex items-center justify-between gap-4 rounded-2xl bg-[#2c4a3b] text-[#f9f6f0] px-6 py-5 lift"
          >
            <span className="flex flex-col gap-1">
              <span className="text-sm font-semibold tracking-wide">
                {t.hero.ctaInsider}
              </span>
              <span className="text-xs font-bold text-[#f0c79f] tracking-wide">
                {t.hero.ctaInsiderNote}
              </span>
            </span>
            <ArrowUpRight
              className="w-5 h-5 shrink-0 transition-transform group-hover:rotate-45"
              aria-hidden
            />
          </a>

          <a
            href="#quiz"
            data-testid="hero-cta-explorer"
            className="group relative flex items-center justify-between gap-4 rounded-2xl border border-[#2c4a3b]/25 bg-white/60 backdrop-blur-md px-6 py-5 text-[#1a2421] lift"
          >
            <span className="text-sm font-semibold tracking-wide">
              {t.hero.ctaExplorer}
            </span>
            <ArrowUpRight
              className="w-5 h-5 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              aria-hidden
            />
          </a>
        </div>

        {/* Decorative stat strip */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6 reveal reveal-delay-4 border-t border-[#2c4a3b]/15 pt-8">
          {[
            ["30+", "Years of research"],
            ["25%", "Avg. savings"],
            ["1:1", "Personal advisor"],
            ["24h", "Plan delivery"],
          ].map(([n, label]) => (
            <div key={label}>
              <p className="font-serif text-4xl md:text-5xl text-[#1a2421]">{n}</p>
              <p className="text-xs uppercase tracking-[0.18em] text-[#5c6b61] mt-2">
                {label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
